import * as React from "react";
import { GoogleApiWrapper, Map, Polygon, InfoWindow } from 'google-maps-react';
import type { StateData, UserType, MapColorScale } from '../../types/geographic';
import { US_STATES_GEOJSON, getStateGeoData } from '../../utils/usStatesGeoJson';
import { getGoogleMapsApiKey, createGoogleMapsConfig } from '../../utils/googleMapsConfig';

// Props interface for the Google Maps component
interface GoogleMapViewProps {
  data: StateData[];
  userType: UserType;
  colorScale: MapColorScale;
  onStateClick?: (state: StateData) => void;
  onStateHover?: (state: StateData | null) => void;
  isLoading?: boolean;
  className?: string;
  google: any; // Google Maps API object
}

// Props for the wrapped component
interface GoogleMapViewWrapperProps extends Omit<GoogleMapViewProps, 'google'> {
  googleMapsApiKey?: string;
}

// Individual state polygon component
const StatePolygon: React.FC<{
  stateData: StateData;
  userType: UserType;
  colorScale: MapColorScale;
  onStateClick?: (state: StateData) => void;
  onStateHover?: (state: StateData | null) => void;
  google: any;
}> = React.memo(({ 
  stateData, 
  userType, 
  colorScale, 
  onStateClick, 
  onStateHover,
  google 
}) => {
  const stateGeoData = getStateGeoData(stateData.state_code);
  
  if (!stateGeoData) return null;
  // Calculate color based on user count
  const getUserValue = (state: StateData): number => {
    switch (userType) {
      case 'clients':
        return state.client_count;
      case 'professionals':
        return state.professional_count;
      default:
        return state.user_count;
    }
  };

  const getColorForValue = (value: number): string => {
    if (value === 0) return colorScale.colors[0];
    
    for (let i = 0; i < colorScale.breaks.length - 1; i++) {
      if (value >= colorScale.breaks[i] && value < colorScale.breaks[i + 1]) {
        return colorScale.colors[i] || colorScale.colors[colorScale.colors.length - 1];
      }
    }
    
    return colorScale.colors[colorScale.colors.length - 1];
  };

  const userValue = getUserValue(stateData);
  const fillColor = getColorForValue(userValue);
  const fillOpacity = userValue > 0 ? 0.7 : 0.3;

  const handleClick = React.useCallback(() => {
    if (onStateClick) {
      onStateClick(stateData);
    }
  }, [stateData, onStateClick]);

  const handleMouseOver = React.useCallback(() => {
    if (onStateHover) {
      onStateHover(stateData);
    }
  }, [stateData, onStateHover]);

  const handleMouseOut = React.useCallback(() => {
    if (onStateHover) {
      onStateHover(null);
    }
  }, [onStateHover]);

  return (
    <Polygon
      paths={stateGeoData.coordinates}
      strokeColor="#ffffff"
      strokeOpacity={0.8}
      strokeWeight={1}
      fillColor={fillColor}
      fillOpacity={fillOpacity}
      onClick={handleClick}
      onMouseover={handleMouseOver}
      onMouseout={handleMouseOut}
      google={google}
    />
  );
});

StatePolygon.displayName = 'StatePolygon';

// Map tooltip component
const MapTooltip: React.FC<{
  state: StateData | null;
  userType: UserType;
  position: google.maps.LatLng | null;
  visible: boolean;
}> = ({ state, userType, position, visible }) => {
  if (!state || !position || !visible) return null;

  return (
    <InfoWindow
      position={position}
      visible={visible}
    >
      <div className="map-info-window" style={{ minWidth: '200px' }}>
        <h6 className="fw-bold mb-2">{state.state_name}</h6>
        <div className="small">
          <div className="mb-1">
            <strong>Total Users:</strong> {state.user_count.toLocaleString()}
          </div>
          <div className="mb-1">
            <strong>Clients:</strong> {state.client_count.toLocaleString()}
          </div>
          <div className="mb-1">
            <strong>Professionals:</strong> {state.professional_count.toLocaleString()}
          </div>
          {state.revenue && state.revenue > 0 && (
            <div className="mb-1">
              <strong>Revenue:</strong> ${state.revenue.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </div>
          )}
          {state.roi && (
            <div className="mb-1">
              <strong>ROI:</strong> {state.roi.toFixed(1)}%
            </div>
          )}
        </div>
      </div>
    </InfoWindow>
  );
};

// Legend component for the map
const MapLegend: React.FC<{
  colorScale: MapColorScale;
  userType: UserType;
}> = ({ colorScale, userType }) => {
  const legendItems = colorScale.breaks.map((breakPoint, index) => {
    if (index === colorScale.breaks.length - 1) return null;
    
    const nextBreak = colorScale.breaks[index + 1];
    const color = colorScale.colors[index];
    
    return {
      color,
      label: index === 0 ? `0` : 
             index === colorScale.breaks.length - 2 ? `${Math.round(breakPoint)}+` :
             `${Math.round(breakPoint)}-${Math.round(nextBreak - 1)}`
    };
  }).filter(Boolean);

  return (
    <div className="map-legend position-absolute bg-white border rounded shadow-sm p-3" style={{
      top: '10px',
      right: '10px',
      zIndex: 1000,
      maxWidth: '200px'
    }}>
      <h6 className="mb-3 fw-semibold">
        {userType === 'clients' ? 'Client' : 
         userType === 'professionals' ? 'Professional' : 'User'} Count
      </h6>
      <div className="d-flex flex-column gap-2">
        {legendItems.map((item, index) => (
          <div key={index} className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "20px",
                height: "16px",
                backgroundColor: item!.color,
                border: "1px solid #ddd",
                borderRadius: "2px"
              }}
            />
            <small className="text-muted">{item!.label}</small>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * GoogleMapView Component
 * 
 * Interactive Google Maps visualization for geographic analytics using choropleth polygons.
 * 
 * Features:
 * - Google Maps integration with polygon overlays for US states
 * - Choropleth visualization (color-coded states based on user density)
 * - Interactive hover tooltips with detailed state information
 * - Click handling for drill-down functionality
 * - Color legend with value ranges
 * - Support for different user types (clients/professionals)
 * - Responsive map controls and zoom
 * 
 * Usage:
 * ```tsx
 * <GoogleMapView
 *   data={statesData}
 *   userType="professionals"
 *   colorScale={colorScale}
 *   onStateClick={(state) => console.log('Clicked:', state)}
 *   onStateHover={(state) => console.log('Hovered:', state)}
 *   google={google}
 * />
 * ```
 */
const GoogleMapView: React.FC<GoogleMapViewProps> = ({
  data,
  userType,
  colorScale,
  onStateClick,
  onStateHover,
  isLoading = false,
  className = "",
  google
}) => {
  const [hoveredState, setHoveredState] = React.useState<StateData | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState<google.maps.LatLng | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);

  // Create state lookup map
  const stateDataMap = React.useMemo(() => {
    const map = new Map<string, StateData>();
    data.forEach(state => {
      map.set(state.state_code, state);
    });
    return map;
  }, [data]);

  // Handle state hover
  const handleStateHover = React.useCallback((state: StateData | null) => {
    setHoveredState(state);
    setShowTooltip(!!state);
    
    if (state) {
      const stateGeoData = getStateGeoData(state.state_code);
      if (stateGeoData) {
        // Use the pre-calculated center for tooltip positioning
        setTooltipPosition(new google.maps.LatLng(stateGeoData.center.lat, stateGeoData.center.lng));
      }
    }
    
    if (onStateHover) {
      onStateHover(state);
    }
  }, [onStateHover, google]);

  // Handle state click
  const handleStateClick = React.useCallback((state: StateData) => {
    if (onStateClick) {
      onStateClick(state);
    }
  }, [onStateClick]);

  // Map configuration
  const mapStyles = [
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "road",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }]
    }
  ];

  if (isLoading) {
    return (
      <div className={`google-map-view ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
            <p className="text-muted mb-0">Loading Google Maps...</p>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`google-map-view ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-map fa-3x text-muted mb-3" aria-hidden="true"></i>
            <h5 className="text-muted">No Geographic Data Available</h5>
            <p className="text-muted mb-0">
              No {userType} data found for the selected time period.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`google-map-view ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-map me-2" aria-hidden="true"></i>
              US States - {userType === 'clients' ? 'Client' : 
                          userType === 'professionals' ? 'Professional' : 'User'} Distribution
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <small className="text-muted">
                <i className="fab fa-google me-1" aria-hidden="true"></i>
                Powered by Google Maps
              </small>
              <small className="text-muted">
                States with data: {data.length}/50
              </small>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          <div style={{ position: 'relative', height: '500px', width: '100%' }}>
            <Map
              google={google}
              zoom={4}
              initialCenter={{ lat: 39.8283, lng: -98.5795 }}
              style={{ width: '100%', height: '100%' }}
              styles={mapStyles}
              disableDefaultUI={false}
              zoomControl={true}
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={true}
            >
              {Object.keys(US_STATES_GEOJSON).map(stateCode => {
                const stateData = stateDataMap.get(stateCode);
                
                if (!stateData) {
                  return null;
                }
                
                return (
                  <StatePolygon
                    key={stateCode}
                    stateData={stateData}
                    userType={userType}
                    colorScale={colorScale}
                    onStateClick={handleStateClick}
                    onStateHover={handleStateHover}
                    google={google}
                  />
                );
              })}
              
              <MapTooltip
                state={hoveredState}
                userType={userType}
                position={tooltipPosition}
                visible={showTooltip}
              />
            </Map>
            
            {/* Legend */}
            <MapLegend
              colorScale={colorScale}
              userType={userType}
            />
          </div>
        </div>
        <div className="card-footer">
          <div className="row">
            <div className="col-md-6">
              <small className="text-muted">
                <i className="fas fa-mouse-pointer me-1" aria-hidden="true"></i>
                Hover states for details • Click to drill down
              </small>
            </div>
            <div className="col-md-6 text-end">
              <small className="text-muted">
                <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                Total {userType}: {data.reduce((sum, state) => {
                  switch (userType) {
                    case 'clients': return sum + state.client_count;
                    case 'professionals': return sum + state.professional_count;
                    default: return sum + state.user_count;
                  }
                }, 0).toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with Google Maps API integration
const GoogleMapViewWrapper: React.FC<GoogleMapViewWrapperProps> = (props) => {
  const config = createGoogleMapsConfig(props.googleMapsApiKey);
  
  // Show error if no API key is available
  if (!config.apiKey) {
    return (
      <div className={`google-map-view ${props.className || ''}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" aria-hidden="true"></i>
            <h5 className="text-warning">Google Maps API Key Required</h5>
            <p className="text-muted mb-3">
              Google Maps API key is required to display the map visualization.
            </p>
            <div className="alert alert-info text-start">
              <small>
                <strong>Setup Instructions:</strong><br />
                Add the following to your Rails view:<br />
                <code>&lt;meta name="google-maps-api-key" content="YOUR_API_KEY"&gt;</code>
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const WrappedComponent = GoogleApiWrapper({
    apiKey: config.apiKey,
    version: config.version,
    language: config.language,
    region: config.region,
    libraries: config.libraries
  })(GoogleMapView);

  return <WrappedComponent {...props} />;
};

GoogleMapView.displayName = 'GoogleMapView';
GoogleMapViewWrapper.displayName = 'GoogleMapViewWrapper';

export default GoogleMapViewWrapper;
export { GoogleMapView };
export type { GoogleMapViewProps, GoogleMapViewWrapperProps };